import { useEffect } from "react";

// Updates document.title + description + canonical + og/twitter/jsonld + hreflang on route change.
// Keeps the static index.html meta as a sensible fallback.
const setMeta = (selector, attr, content) => {
  if (!content) return;
  let el = document.head.querySelector(selector);
  if (!el) {
    el = document.createElement(selector.split("[")[0]);
    const match = selector.match(/\[(\w+)="([^"]+)"\]/);
    if (match) el.setAttribute(match[1], match[2]);
    document.head.appendChild(el);
  }
  el.setAttribute(attr, content);
};

const upsertJsonLd = (id, payload) => {
  let el = document.head.querySelector(`script[type="application/ld+json"][data-seo="${id}"]`);
  if (payload == null) {
    if (el) el.remove();
    return;
  }
  if (!el) {
    el = document.createElement("script");
    el.type = "application/ld+json";
    el.setAttribute("data-seo", id);
    document.head.appendChild(el);
  }
  el.textContent = JSON.stringify(payload);
};

/**
 * @param {object} opts
 * @param {string} opts.title            - Page title
 * @param {string} opts.description      - Meta description
 * @param {string} opts.canonical        - Canonical URL
 * @param {string} [opts.image]          - OG/Twitter image URL
 * @param {string} [opts.type="website"] - og:type (website | article)
 * @param {object} [opts.article]        - { datePublished, dateModified, author, section }
 * @param {Array<{name,url}>} [opts.breadcrumbs] - Ordered list for BreadcrumbList schema
 * @param {object} [opts.alternates]     - { fr, en } URLs for hreflang; x-default = fr
 */
export default function useSeo({ title, description, canonical, image, type = "website", article, breadcrumbs, alternates, definedTerms }) {
  useEffect(() => {
    if (title) document.title = title;
    if (description) setMeta('meta[name="description"]', "content", description);
    if (canonical) setMeta('link[rel="canonical"]', "href", canonical);
    if (title) setMeta('meta[property="og:title"]', "content", title);
    if (description) setMeta('meta[property="og:description"]', "content", description);
    if (image) setMeta('meta[property="og:image"]', "content", image);
    if (canonical) setMeta('meta[property="og:url"]', "content", canonical);
    setMeta('meta[property="og:type"]', "content", type);
    if (title) setMeta('meta[name="twitter:title"]', "content", title);
    if (description) setMeta('meta[name="twitter:description"]', "content", description);
    if (image) setMeta('meta[name="twitter:image"]', "content", image);

    // Hreflang alternates — inserted fresh on every route so a user navigating
    // /equipe → /blog doesn't inherit /equipe's hreflang. Removes stale <link rel="alternate"
    // hreflang="*"> then reinstalls. Resolves the "hreflang points to non-canonical page"
    // warning by guaranteeing each hreflang href matches the canonical we just set.
    if (alternates && (alternates.fr || alternates.en)) {
      document.head.querySelectorAll('link[rel="alternate"][hreflang]').forEach((el) => el.remove());
      const add = (hreflang, href) => {
        if (!href) return;
        const el = document.createElement("link");
        el.setAttribute("rel", "alternate");
        el.setAttribute("hreflang", hreflang);
        el.setAttribute("href", href);
        document.head.appendChild(el);
      };
      add("fr", alternates.fr);
      add("en", alternates.en);
      add("x-default", alternates.fr || alternates.en);
    }

    // Article JSON-LD (BlogPosting) for blog articles — helps Google Discover and AI grounding.
    if (type === "article" && title && canonical) {
      upsertJsonLd("article", {
        "@context": "https://schema.org",
        "@type": "BlogPosting",
        headline: title.replace(/ — Cabinet ECHO.*$/, "").trim(),
        description,
        image: image ? [image] : undefined,
        mainEntityOfPage: { "@type": "WebPage", "@id": canonical },
        datePublished: article?.datePublished,
        dateModified: article?.dateModified || article?.datePublished,
        author: {
          "@type": "Organization",
          name: "Cabinet ECHO",
          url: "https://cabinet-echo.com/",
        },
        publisher: {
          "@type": "Organization",
          name: "Cabinet ECHO",
          logo: { "@type": "ImageObject", url: "https://cabinet-echo.com/apple-touch-icon.png" },
        },
        articleSection: article?.section || undefined,
        inLanguage: document.documentElement.lang || "fr-FR",
      });
    } else if (type === "creative_work" && title && canonical) {
      // Project / portfolio pages — a CreativeWork is more accurate than BlogPosting:
      // - Tells Google this is a portfolio piece, not editorial content
      // - Avoids Search Console "BlogPosting requires author/dateModified" warnings on
      //   project pages where these don't really apply
      // - Surfaces better in image search and Knowledge Graph as a piece of work
      upsertJsonLd("article", {
        "@context": "https://schema.org",
        "@type": "CreativeWork",
        name: title.replace(/ — Cabinet ECHO.*$/, "").replace(/ · Cabinet ECHO.*$/, "").trim(),
        description,
        image: image ? [image] : undefined,
        url: canonical,
        dateCreated: article?.datePublished,
        creator: {
          "@type": "Organization",
          name: "Cabinet ECHO",
          url: "https://cabinet-echo.com/",
        },
        locationCreated: article?.section
          ? { "@type": "Place", name: article.section }
          : undefined,
        inLanguage: document.documentElement.lang || "fr-FR",
        keywords: article?.keywords || undefined,
      });
    } else {
      upsertJsonLd("article", null);
    }

    // Breadcrumb JSON-LD
    if (breadcrumbs?.length) {
      upsertJsonLd("breadcrumb", {
        "@context": "https://schema.org",
        "@type": "BreadcrumbList",
        itemListElement: breadcrumbs.map((b, i) => ({
          "@type": "ListItem",
          position: i + 1,
          name: b.name,
          item: b.url,
        })),
      });
    } else {
      upsertJsonLd("breadcrumb", null);
    }

    // DefinedTermSet JSON-LD (glossary) — surfaces individual definitions in Google
    // rich results ("definition" snippet) and feeds AI grounding tools like Perplexity
    // and ChatGPT-search with structured term/short/long triples.
    if (definedTerms?.terms?.length && canonical) {
      upsertJsonLd("defined-term-set", {
        "@context": "https://schema.org",
        "@type": "DefinedTermSet",
        name: definedTerms.name || "Glossaire",
        description: definedTerms.description,
        url: canonical,
        inLanguage: document.documentElement.lang || "fr-FR",
        hasDefinedTerm: definedTerms.terms.map((t) => ({
          "@type": "DefinedTerm",
          name: t.term,
          description: t.long || t.short,
          termCode: t.code || undefined,
          url: t.url || undefined,
          inDefinedTermSet: canonical,
        })),
      });
    } else {
      upsertJsonLd("defined-term-set", null);
    }
  }, [title, description, canonical, image, type, article, breadcrumbs, alternates, definedTerms]);
}
